import { isValidElement, useEffect, useMemo, useRef } from 'react';
import { KEY_RECOIL, AUTO_EVENTS, AUTO_EVENT_TAB_ENUM } from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { selectActiveTab, selectChangeStripData, useReportStore } from '@/entities/report';
import type { EventPreviewStripProps } from './index';

/**
 * Backs one event strip preview tile in the strips-management tab. Reads the tab's active event button and pending strip changes from the report store, looks up any pending change for this event so the tile can flag it, derives whether the event type badge should show for the auto-event tabs, supplies the guarded click handler, and clears the tile's stored timeout for the active button on unmount.
 *
 * @param props - Tile props carrying the event, its index, the overlay content and onClickSelectEvent.
 * @returns The tile view model with onClick, the matching pending change eleChange, and showTypeEvent.
 */
const useEventPreviewStrip = ({
  event,
  overlayContent,
  index,
  onClickSelectEvent,
}: Pick<EventPreviewStripProps, 'event' | 'overlayContent' | 'index' | 'onClickSelectEvent'>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const eleChange = changeStripData.find((x) => x.id === event?.id);
  const timeoutRef = useRef<any>();
  /**
   * Handles a click on an event strip preview, selecting that event unless the click is a multi-select or overlay interaction. Swallows the event when an overlay element is rendered or shift, ctrl or meta is held, otherwise forwards the event and its index to the parent selection handler.
   *
   * @param e - Mouse event from the strip preview.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    // Prevent click to select new event,shift or ctrl when select multiple
    if (e && (isValidElement(overlayContent ?? null) || e?.shiftKey || e?.ctrlKey || e?.metaKey)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    onClickSelectEvent(event, index);
  };

  const showTypeEvent = useMemo(
    () =>
      (activeButton as any) === AUTO_EVENTS ||
      activeButton === AUTO_EVENT_TAB_ENUM.OTHER ||
      activeButton === AUTO_EVENT_TAB_ENUM.VE ||
      activeButton === AUTO_EVENT_TAB_ENUM.SVE ||
      activeButton === AUTO_EVENT_TAB_ENUM.HB,
    [activeButton],
  );

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton as any, timeoutRef.current);
    },
    [activeButton],
  );

  return {
    onClick,
    eleChange,
    showTypeEvent,
  };
};

export default useEventPreviewStrip;
